import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import {
  CreateGuardianAttendancePayload,
  GuardianAttendanceApi,
  GuardianAttendanceRecord,
} from '@/services/guardian-attendance.api';
import { SchoolsApi } from '@/services/superadmin.api';
import { OrgPortalApi } from '@/services/org.api';
import { useAuthStore } from '@/stores/auth.store';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Input, Select } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { DataTable, useDataTable } from '@/components/data-table';

const columnHelper = createColumnHelper<GuardianAttendanceRecord>();

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

/**
 * Combines the old system's separate "Create Parent Attendance" and "View
 * Attendance (Parent)" screens into one — same merge Student/Teacher
 * attendance already got in this app (a single "View Attendance" page that
 * both browses and marks), just applied here too for consistency. Real
 * legacy keeps these as two separate routes, but that split isn't
 * something worth preserving on our side — it was pure extra navigation.
 */
export function ParentAttendancePage({ scope }: { scope: 'superadmin' | 'organization' | 'school' }) {
  const qc = useQueryClient();
  const schoolIdFromAuth = useAuthStore((s) => s.user?.schoolId);
  const showSchoolPicker = scope !== 'school';
  const dt = useDataTable({ defaultLimit: 20 });

  const [schoolId, setSchoolId] = useState('');
  // Always defaults to today — never left blank. The old View page seeded
  // this from "the most recent date with data" and never fell back when
  // that query came back empty (a school with zero attendance history, or
  // simply the query still loading), so the table silently never rendered
  // at all — looked indistinguishable from a broken page. Today is always
  // a valid, predictable starting point; picking an older date is one
  // click away via the date input itself.
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [guardianId, setGuardianId] = useState('');
  const [type, setType] = useState<0 | 1>(1);
  const [notice, setNotice] = useState<string | null>(null);

  const effectiveSchoolId = showSchoolPicker ? schoolId : schoolIdFromAuth ?? '';

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-parent-attendance', scope],
    queryFn: async () =>
      scope === 'organization' ? (await OrgPortalApi.listSchools({ limit: 100 })).items : (await SchoolsApi.list({ limit: 100 })).items,
    enabled: showSchoolPicker,
  });

  const { data: guardians = [], isFetching: guardiansLoading } = useQuery({
    queryKey: ['guardian-attendance-guardians', effectiveSchoolId],
    queryFn: () =>
      GuardianAttendanceApi.guardiansForSchool({ schoolId: showSchoolPicker ? effectiveSchoolId : undefined }),
    enabled: !!effectiveSchoolId,
  });

  const { data, isFetching } = useQuery({
    queryKey: ['guardian-attendance', dt.params, effectiveSchoolId, date],
    queryFn: () =>
      GuardianAttendanceApi.list({
        ...dt.params,
        schoolId: showSchoolPicker ? effectiveSchoolId || undefined : undefined,
        date: date || undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const create = useMutation({
    mutationFn: (payload: CreateGuardianAttendancePayload) => GuardianAttendanceApi.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['guardian-attendance'] });
      setNotice('Attendance recorded.');
      setGuardianId('');
    },
  });

  const submitValid = !!effectiveSchoolId && !!guardianId && !!date;

  const submit = () => {
    setNotice(null);
    create.mutate({
      schoolId: showSchoolPicker ? effectiveSchoolId : undefined,
      guardianId,
      type,
      date,
    });
  };

  const columns = [
    columnHelper.display({
      id: 'guardianName',
      header: 'Guardian',
      cell: (info) => info.row.original.guardian?.firstName || info.row.original.guardian?.username || '—',
    }),
    ...(showSchoolPicker
      ? [
          columnHelper.display({
            id: 'school',
            header: 'School',
            cell: (info: any) => info.row.original.school?.name ?? '—',
          }),
        ]
      : []),
    columnHelper.accessor('type', {
      id: 'type',
      header: 'Status',
      cell: (info) => (
        <Badge tone={info.getValue() === 1 ? 'green' : 'red'}>{info.getValue() === 1 ? 'Present' : 'Absent'}</Badge>
      ),
    }),
    columnHelper.accessor('date', { id: 'date', header: 'Date' }),
  ];

  return (
    <>
      <PageHeader title="Parent Attendance" description="Record and browse guardian attendance for any date." />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {showSchoolPicker && (
          <Select className="!w-56" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
            <option value="">All schools</option>
            {schools.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        )}
        <Input type="date" className="!w-40" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>

      {(!showSchoolPicker || effectiveSchoolId) && (
        <div className="card mb-4 flex flex-wrap items-end gap-3 p-4">
          <Field label="Guardian" required className="!mb-0 w-56">
            <Select
              value={guardianId}
              onChange={(e) => setGuardianId(e.target.value)}
              disabled={guardiansLoading}
            >
              <option value="">{guardiansLoading ? 'Loading…' : '— Select guardian —'}</option>
              {guardians.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Status" required className="!mb-0 w-36">
            <Select value={type} onChange={(e) => setType(Number(e.target.value) as 0 | 1)}>
              <option value={1}>Present</option>
              <option value={0}>Absent</option>
            </Select>
          </Field>
          <Field label="Date" required className="!mb-0 w-40">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <button className="btn-primary" onClick={submit} disabled={!submitValid || create.isPending}>
            {create.isPending ? 'Recording…' : 'Record attendance'}
          </button>
          {notice && <div className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">{notice}</div>}
          {create.error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(create.error)}</div>
          )}
          <p className="w-full text-xs text-slate-500">Changing the date above also updates the table below.</p>
        </div>
      )}

      <DataTable
        columns={columns}
        result={data}
        state={dt}
        loading={isFetching}
        emptyMessage="No guardian attendance recorded for this date."
        getRowId={(row) => row.id}
      />
    </>
  );
}
